import { Component, OnInit, OnDestroy, OnChanges, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, takeUntil, take } from 'rxjs';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { AppState } from '../../../../state/app.state';
import { Workspace } from '../../../../models/workspace.model';
import * as WorkspaceSelectors from '../../../../state/workspace/workspace.selectors';
import * as WorkspaceActions from '../../../../state/workspace/workspace.actions';

@Component({
  selector: 'app-change-workspace',
  templateUrl: './change-workspace.component.html',
  styleUrls: ['./change-workspace.component.scss'],
  animations: [
    trigger('dropdownAnimation', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'scale(0.95)'
        }),
        animate('100ms ease-out', style({
          opacity: 1,
          transform: 'scale(1)'
        }))
      ]),
      transition(':leave', [
        animate('75ms ease-in', style({
          opacity: 0,
          transform: 'scale(0.95)'
        }))
      ])
    ])
  ]
})
export class ChangeWorkspaceComponent implements OnInit, OnDestroy, OnChanges {
  @Input() currentWorkspaceId: string | null = null;
  @Input() workspace: Workspace | null = null; // Add workspace input
  @Input() showDetails: boolean = false;
  @Input() showSearch: boolean = true;
  @Input() showCreateOption: boolean = false;
  @Input() dropdownPosition: 'left' | 'right' = 'left';
  @Input() disabled: boolean = false;

  @Output() workspaceChanged = new EventEmitter<Workspace>();
  @Output() createWorkspace = new EventEmitter<void>();

  showDropdown = false;
  currentWorkspace: Workspace | null = null;
  workspaces: Workspace[] = [];
  filteredWorkspaces: Workspace[] = [];
  searchQuery = '';
  loading = false;
  workspacesLoaded = false; // Track if workspaces have been loaded

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Use the passed workspace object directly
    this.setCurrentWorkspace();

    // Subscribe to workspace changes but don't trigger loading
    this.subscribeToWorkspaceChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleDropdown(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.disabled) {
      return;
    }

    this.showDropdown = !this.showDropdown;

    if (this.showDropdown) {
      // Opening dropdown - load workspaces if not already loaded
      if (!this.workspacesLoaded) {
        this.loadWorkspaces();
      }
      this.searchQuery = '';
      this.filteredWorkspaces = [...this.workspaces];
    }
  }

  loadWorkspaces(): void {
    this.loading = true;
    this.workspacesLoaded = true;

    // Try just selecting from the store without dispatching anything
    this.store.select(WorkspaceSelectors.selectAllWorkspaces).pipe(take(1)).subscribe(workspaces => {
      if (workspaces && workspaces.length > 0) {
        this.workspaces = workspaces;
        this.filteredWorkspaces = [...workspaces];
        this.loading = false;
      } else {
        // If no workspaces in store, show test data
        this.workspaces = [
          { id: '1', name: 'Test Workspace 1', initials: 'TW', color: 'bg-blue-500' },
          { id: '2', name: 'Test Workspace 2', initials: 'TW', color: 'bg-green-500' }
        ] as any;
        this.filteredWorkspaces = [...this.workspaces];
        this.loading = false;
      }
    });
  }

  setCurrentWorkspace(): void {
    // Use the passed workspace object directly
    if (this.workspace) {
      this.currentWorkspace = this.workspace;
      this.currentWorkspaceId = this.workspace.id;
    } else if (this.currentWorkspaceId) {
      // Fallback to placeholder if only ID is provided
      this.currentWorkspace = {
        id: this.currentWorkspaceId,
        name: 'Select Workspace',
        initials: '?',
        color: 'bg-gray-500'
      } as Workspace;
    }
  }

  subscribeToWorkspaceChanges(): void {
    // Subscribe to all workspaces - only updates when loaded
    this.store.select(WorkspaceSelectors.selectAllWorkspaces)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (workspaces) => {
          if (workspaces && workspaces.length > 0) {
            this.workspaces = workspaces;
            this.filteredWorkspaces = [...workspaces];
            this.loading = false;

            // Update current workspace display with real data if we don't have workspace object
            // BUT DON'T emit any events or trigger navigation
            if (!this.workspace && this.currentWorkspaceId) {
              const found = workspaces.find(w => w.id === this.currentWorkspaceId);
              if (found) {
                this.currentWorkspace = found;
                // DON'T emit workspaceChanged here - this is just for display
              }
            }
          } else {
            this.workspaces = [];
            this.filteredWorkspaces = [];
          }
        },
      });

    this.store.select(WorkspaceSelectors.selectWorkspacesLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.loading = loading;
      });
  }

  filterWorkspaces(): void {
    if (!this.searchQuery.trim()) {
      this.filteredWorkspaces = [...this.workspaces];
    } else {
      const query = this.searchQuery.toLowerCase();
      this.filteredWorkspaces = this.workspaces.filter(workspace =>
        workspace.name.toLowerCase().includes(query)
      );
    }
  }

  selectWorkspace(workspace: Workspace, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.showDropdown = false; // Close dropdown first

    if (workspace.id !== this.currentWorkspaceId) {
      this.currentWorkspace = workspace;
      this.currentWorkspaceId = workspace.id;
      this.workspaceChanged.emit(workspace);
      // Don't navigate here - let the parent component handle navigation
    }
  }

  createNewWorkspace(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.showDropdown = false;
    this.createWorkspace.emit();
    // You can also navigate to create workspace page
    // this.router.navigate(['/workspaces/create']);
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.workspace-dropdown-container')) {
      this.showDropdown = false;
    }
  }

  // Handle keyboard navigation
  @HostListener('keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.showDropdown) {
      switch (event.key) {
        case 'Escape':
          this.showDropdown = false;
          event.preventDefault();
          break;
        case 'ArrowDown':
          // Implement navigation logic if needed
          event.preventDefault();
          break;
        case 'ArrowUp':
          // Implement navigation logic if needed
          event.preventDefault();
          break;
      }
    }
  }

  // Handle input changes (when workspace object is updated from parent)
  ngOnChanges(changes: any): void {
    if (changes.workspace && changes.workspace.currentValue) {
      this.setCurrentWorkspace();
    }
  }
}
